import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './LandingPage.css';

/**
 * LandingPage — Public-facing front door for new visitors.
 * Goal: convert anonymous visitors into registered players in one click.
 *
 * Logged-in users are redirected to /characters automatically so this page
 * is only seen on the very first visit (or after logout).
 */
const LandingPage = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) {
      navigate('/characters', { replace: true });
    }
  }, [loading, user, navigate]);

  // Avoid a flash of landing content while we resolve auth state
  if (loading || user) return null;

  return (
    <div className="landing-page">
      <div className="landing-hero">
        <h1 className="landing-title">The Realms of Loria</h1>
        <p className="landing-tagline">
          A persistent multiplayer fantasy world. Roll a hero, hunt monsters,
          shape your destiny — all in your browser.
        </p>

        <div className="landing-cta">
          <Link to="/register" className="landing-btn landing-btn-primary">
            Play Now — Free
          </Link>
          <Link to="/login" className="landing-btn landing-btn-secondary">
            Sign In
          </Link>
        </div>

        <p className="landing-quickstart">
          New here? Hit <strong>Play Now</strong> → use the green
          <strong> Quick Start</strong> button on registration to be in-game
          in under 30 seconds.
        </p>
      </div>

      <div className="landing-features">
        <div className="landing-feature">
          <div className="landing-feature-icon">⚔️</div>
          <h3>Card-Driven Combat</h3>
          <p>
            Build a deck from class abilities, level-up cards, and spells.
            Every battle is a tactical puzzle — no two turns feel the same.
          </p>
        </div>
        <div className="landing-feature">
          <div className="landing-feature-icon">🌍</div>
          <h3>One Living World</h3>
          <p>
            Every player shares the same world map. Meet other adventurers
            on the road, group up for delves, or trade in the market.
          </p>
        </div>
        <div className="landing-feature">
          <div className="landing-feature-icon">🧝</div>
          <h3>Six Races, Six Classes</h3>
          <p>
            Human, Elf, Dwarf, Halfling, Half-Elf, Half-Orc. Fighter, Mage,
            Cleric, Thief, Psion, Druid. Mix and match — your race shapes
            dialogue, your class shapes your deck.
          </p>
        </div>
        <div className="landing-feature">
          <div className="landing-feature-icon">📜</div>
          <h3>Branching Quests</h3>
          <p>
            70+ quests across good, neutral, and evil paths. Choices matter —
            mid-chain decisions can lock you out of one alignment and open
            another.
          </p>
        </div>
        <div className="landing-feature">
          <div className="landing-feature-icon">🃏</div>
          <h3>Loot, Craft, Trade</h3>
          <p>
            Drop rare gear, harvest monster parts, forge equipment at
            crafting stations, and sell on the player-driven market.
          </p>
        </div>
        <div className="landing-feature">
          <div className="landing-feature-icon">🛡️</div>
          <h3>Free to Play</h3>
          <p>
            No ads, no pay-to-win. Cosmetic Crown Shop only. The whole game
            is open from level 1 — your skill and your dice rolls decide
            how far you climb.
          </p>
        </div>
      </div>

      <div className="landing-footer">
        <Link to="/code-of-conduct" className="landing-footer-link">
          Code of Conduct
        </Link>
        <span className="landing-footer-sep">•</span>
        <Link to="/login" className="landing-footer-link">
          Returning player? Sign in
        </Link>
      </div>
    </div>
  );
};

export default LandingPage;
